import {Component, ElementRef, EventEmitter, Input, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {MatrimonialProfileService} from '../api/matrimonial-profile.service';
import {MatrimonialProfile} from '../models/matrimonial-profile';
import {AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators} from '@angular/forms';
import {ToastrService} from 'ngx-toastr';
import {finalize} from 'rxjs';
import {SpinnerService} from '../shared/services/spinner.service';
import {API_URL} from '../constants';

@Component({
    selector: 'app-matrimonial',
    templateUrl: './matrimonial.component.html',
    styleUrls: ['./matrimonial.component.css']
})
export class MatrimonialComponent implements OnInit {
    @Output() profileViewModeChange = new EventEmitter<'list' | 'detail' | 'form'>();
    @ViewChild('canvas', {static: false}) canvas!: ElementRef<HTMLCanvasElement>;
    mAboutMe: string = ''; // Store editor HTML content
    mExpectations: string = '';
    profiles: MatrimonialProfile[] = [];
    selectedProfile: MatrimonialProfile | null = null;
    profileForm!: FormGroup;
    isEditing = false;
    loading = false;
    userId = sessionStorage.getItem('userId') || '';
    viewMode: 'list' | 'form' | 'detail' = 'list';
    page: number = 1;
    userRole: any;
    userGroup: any;
    userChapter: any;
    selectedFile: File | null = null;
    uploadedImageUrl: string | null = null;
    searchTerm = '';
    filteredProfiles: MatrimonialProfile[] = [];

    @Input() mode: 'approved' | 'pending' = 'approved';


    constructor(
        private profileService: MatrimonialProfileService,
        private fb: FormBuilder,
        private toastr: ToastrService,
        private spinner: SpinnerService
    ) {
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['mode']) {
            if (this.mode === 'pending') {
                this.openApprovalListOnly();
            } else {
                this.loadProfiles();
            }
        }
    }


    ngOnInit(): void {
        this.initForm();
        this.loadProfiles();
        this.userChapter = sessionStorage.getItem('chapter');
        this.userRole = JSON.parse(sessionStorage.getItem('rolesPermission') || '{}');
        this.userGroup = sessionStorage.getItem('userGroup') || '{}';
        this.mAboutMe = '';
        this.mExpectations = '';
    }

    private initForm(): void {
        this.profileForm = this.fb.group({
            profileId: [''],
            createdBy: this.userId,
            firstName: ['', Validators.required],
            lastName: ['', Validators.required],
            gender: ['', Validators.required],
            dob: ['', [Validators.required, ageValidator(18)]],
            age: [''],
            education: [''],
            profession: [''],
            height: [''],
            gotra: [''],
            city: [''],
            state: [''],
            country: [''],
            aboutMe: [''],
            expectations: [''],
            email: ['', [Validators.required, Validators.email]],
            phone: ['', Validators.required],
            status: ['PENDING'],
            photoUrl: [''],
            acknowledge: [false, Validators.requiredTrue]
        });
    }


    loadProfiles(): void {
        this.loading = true;

        this.profileService.getProfiles().subscribe({
            next: (data) => {
                this.profiles = data
                    .filter(p =>
                        p.status?.toUpperCase() === 'APPROVED' ||
                        (p.status?.toUpperCase() === 'PENDING' && p.createdBy === this.userId)
                    )
                    .map(p => ({
                        ...p,
                        imageUrl: p.photoUrl
                            ? `${API_URL}profile/viewimage/${p.profileId}/${p.photoUrl}`
                            : 'assets/default-profile.png'
                    }));
                this.applyFilter();
                this.loading = false;
            },
            error: (err) => {
                console.error('Error loading profiles', err);
                this.loading = false;
            }
        });
    }

    applyFilter(): void {
        const term = (this.searchTerm || '').toLowerCase().trim();
        this.filteredProfiles = !term ? this.profiles : this.profiles.filter(p =>
            `${p.firstName} ${p.lastName} ${p.profession} ${p.city}`.toLowerCase().includes(term)
        );
        this.page = 1;
    }

    showPending: boolean = false;

    toggleProfileList(): void {
        if (this.showPending) {
            // If currently showing pending, load full profile list
            this.loadProfiles();
        } else {
            // Otherwise, show only pending profiles
            this.openApprovalListOnly();
        }
        // Toggle the flag
        this.showPending = !this.showPending;
    }

    onSubmit(): void {
        if (this.profileForm.invalid) {
            this.toastr.error('Please fix the errors in the form.', 'Validation Failed');
            this.profileForm.markAllAsTouched();
            return;
        }

        const profile: MatrimonialProfile = this.profileForm.value;
        profile.createdBy = this.userId

        if (this.isEditing && profile.profileId) {
            this.profileService.updateProfile(profile.profileId, profile).subscribe({
                next: () => {
                    this.loadProfiles();
                    this.resetForm();
                    this.toastr.success('Profile updated successfully!', 'Success');
                    if (this.selectedFile) {
                        this.uploadImage(profile.profileId || '');
                    }
                    this.viewProfile(profile);
                },
                error: () => this.toastr.error('Failed to update profile', 'Error')
            });
        } else {

            this.profileService.createProfile(profile).subscribe({
                next: (createdProfile) => {
                    this.loadProfiles();
                    this.resetForm();
                    this.toastr.success('Profile created successfully!', 'Success');
                    // 🔹 If user had selected a file before submitting, upload it now
                    if (this.selectedFile && createdProfile.profileId) {
                        this.uploadImage(createdProfile.profileId);
                    }
                },
                error: () => this.toastr.error('Failed to create profile', 'Error')
            });
        }
    }

    openEditForm(profile: MatrimonialProfile): void {
        this.isEditing = true;
        this.profileForm.patchValue(profile);
        this.mAboutMe = profile?.aboutMe || '';
        this.mExpectations = profile?.expectations || '';

        this.viewMode = 'form';
        if (profile.photoUrl) {
            this.uploadedImageUrl = `${API_URL}profile/viewimage/${profile.profileId}/${profile.photoUrl}`;
            this.profileService.getProfileImageAsBlob(this.uploadedImageUrl).subscribe(
                (blob: Blob) => {
                    if (blob && blob.size > 0) {
                        if (this.uploadedImageUrl) {
                            URL.revokeObjectURL(this.uploadedImageUrl);
                        }
                        this.uploadedImageUrl = URL.createObjectURL(blob); // Bind to <img [src]>
                    } else {
                        console.error('Blob is empty or invalid.');
                    }
                },
                (error) => {
                    console.error('Failed to load image preview:', error);
                }
            );
        }
    }

    imageUrl: string = 'assets/img/profile/default-user.png';

    viewProfile(profile: MatrimonialProfile): void {
        this.selectedProfile = profile;
        this.mAboutMe = this.selectedProfile?.aboutMe || '';
        this.mExpectations = this.selectedProfile?.expectations || '';
        this.viewMode = 'detail';
        this.profileViewModeChange.emit('detail');
        this.imageUrl = profile.photoUrl
            ? `${API_URL}profile/viewimage/${profile.profileId}/${profile.photoUrl}`
            : 'assets/img/profile/default-user.png';
    }

    editProfile(profile: MatrimonialProfile): void {
        this.isEditing = true;
        this.profileForm.patchValue(profile);
        if (profile.photoUrl) {
            this.uploadedImageUrl = `${API_URL}profile/viewimage/${profile.profileId}/${profile.photoUrl}`;
            this.profileService.getProfileImageAsBlob(this.uploadedImageUrl).subscribe(
                (blob: Blob) => {
                    if (blob && blob.size > 0) {
                        if (this.uploadedImageUrl) {
                            URL.revokeObjectURL(this.uploadedImageUrl);
                        }
                        this.uploadedImageUrl = URL.createObjectURL(blob); // Bind to <img [src]>
                    } else {
                        console.error('Blob is empty or invalid.');
                    }
                },
                (error) => {
                    console.error('Failed to load image preview:', error);
                }
            );
        }
    }


    deleteProfile(profileId?: string): void {
        if (!profileId) return;
        if (!confirm('Are you sure you want to delete this profile?')) return;

        this.profileService.deleteProfile(profileId).subscribe({
            next: () => {
                this.loadProfiles();
                this.toastr.success('Profile deleted successfully!', 'Success');
            },
            error: () => this.toastr.error('Failed to delete profile', 'Error')
        });
    }

    resetForm(): void {
        this.profileForm.reset({status: 'PENDING'});
        this.isEditing = false;
        this.selectedProfile = null;
        this.uploadedImageUrl = null;
    }


    onFileSelect(event: any, profileId?: string) {
        this.selectedFile = event.target.files[0];

        if (!this.selectedFile) return;

        if (profileId) {
            // Existing profile → upload immediately
            this.uploadImage(profileId);
        } else {
            // New profile → just preview, upload after profile is created
            const reader = new FileReader();
            reader.onload = (e: any) => {
                this.uploadedImageUrl = e.target.result; // show preview
            };
            reader.readAsDataURL(this.selectedFile);
        }
    }


    // ✅ Upload image as Base64 (like ProfileComponent)
    uploadImage(profileId: string): void {
        if (this.selectedFile) {
            const reader = new FileReader();
            reader.onloadend = () => {
                const result = reader.result as string;
                const base64String = result.split(',')[1];

                this.spinner.show();
                this.profileService.uploadBase64Image(profileId, base64String, this.selectedFile!.name).pipe(
                    finalize(() => this.spinner.hide())
                ).subscribe({
                    next: (response) => {
                        // this.toastr.success('Photo uploaded successfully!', 'Success');
                        if (response && response.photoUrl && response.photoUrl !== 'null') {
                            this.profileForm.patchValue({photoUrl: response.photoUrl});
                            //this.uploadedImageUrl = `${API_URL}profile/viewimage/${profileId}/${response.photoUrl}`;
                            const imageUrl = `${API_URL}profile/viewimage/${profileId}/${response.photoUrl}`;
                            this.profileService.getProfileImageAsBlob(imageUrl).subscribe(
                                (blob: Blob) => {
                                    if (blob && blob.size > 0) {
                                        if (this.uploadedImageUrl) {
                                            URL.revokeObjectURL(this.uploadedImageUrl);
                                        }
                                        this.uploadedImageUrl = URL.createObjectURL(blob); // Bind to <img [src]>
                                    } else {
                                        console.error('Blob is empty or invalid.');
                                    }
                                },
                                (error) => {
                                    console.error('Failed to load image preview:', error);
                                }
                            );


                        }
                    },
                    error: () => this.toastr.error('Image upload failed.', 'Error')
                });
            };
            reader.readAsDataURL(this.selectedFile);
        }
    }


    openCreateForm(): void {
        this.resetForm();
        this.isEditing = false;
        this.viewMode = 'form';
    }

    backToList(): void {
        this.resetForm();
        this.viewMode = 'list';
        this.profileViewModeChange.emit('list');
    }


    /**
     * Validate the age based on DOB.
     * Returns true if valid, false otherwise.
     * Optionally, sets the age in the form.
     */
    validateAge(): boolean {
        const dobValue = this.profileForm.get('dob')?.value;
        if (!dobValue) {
            return false; // DOB not provided
        }

        const dob = new Date(dobValue);
        const today = new Date();

        let age = today.getFullYear() - dob.getFullYear();
        const monthDiff = today.getMonth() - dob.getMonth();
        const dayDiff = today.getDate() - dob.getDate();

        // Adjust age if birthday hasn't occurred yet this year
        if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
            age--;
        }

        // Update the age field in the form
        this.profileForm.patchValue({age});

        // Minimum age check
        if (age < 18) {
            this.toastr.error('Age must be at least 18 based on the date of birth.', 'Validation Error');
            return false;
        }

        return true;
    }

    getAge(dob: string | Date): number {
        if (!dob) return 0;

        const birthDate = new Date(dob);
        const today = new Date();

        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        const dayDiff = today.getDate() - birthDate.getDate();

        // Adjust if birthday hasn't occurred yet this year
        if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
            age--;
        }

        return age;
    }

    onImageError(event: Event) {
        (event.target as HTMLImageElement).src = 'assets/img/profile/default-user.png';
    }


    openApprovalListOnly(): void {
        this.loading = true;

        this.profileService.getProfiles().subscribe({
            next: (data) => {
                // Filter only pending profiles
                const pendingProfiles = data.filter(
                    profile => profile.status?.toUpperCase() === 'PENDING'
                );

                // Map each profile and fetch its image blob asynchronously
                this.profiles = pendingProfiles.map(profile => ({
                    ...profile,
                    imageUrl: 'assets/default-profile.png' // default placeholder
                }));
                this.applyFilter();

                // For each profile, fetch image and replace when ready
                this.profiles.forEach(profile => {
                    if (profile.photoUrl && profile.photoUrl !== 'null') {
                        const imageUrl = `${API_URL}profile/viewimage/${profile.profileId}/${profile.photoUrl}`;
                        this.profileService.getProfileImageAsBlob(imageUrl).subscribe({
                            next: (blob: Blob) => {
                                if (blob && blob.size > 0) {
                                    const objectURL = URL.createObjectURL(blob);
                                    profile.imageUrl = objectURL; // dynamically update in UI
                                } else {
                                    console.warn('Empty blob for profile:', profile.profileId);
                                }
                            },
                            error: (err) => {
                                console.error('Failed to load image for profile', profile.profileId, err);
                            }
                        });
                    }
                });

                this.viewMode = 'list';
                this.loading = false;
            },
            error: (err) => {
                console.error('Error loading pending profiles:', err);
                this.toastr.error('Failed to load pending profiles.');
                this.loading = false;
            }
        });
    }

    approveProfile(profileId: string): void {
        if (!confirm('Are you sure you want to approve this profile?')) {
            return;
        }

        this.loading = true;

        this.profileService.approveProfile(profileId).subscribe({
            next: (updatedProfile) => {
                this.toastr.success('Profile approved successfully.');

                // Update UI instantly
                this.profiles = this.profiles.map(p =>
                    p.profileId === updatedProfile.profileId ? updatedProfile : p
                );
                this.applyFilter();
                if (this.selectedProfile?.profileId === updatedProfile.profileId) {
                    this.selectedProfile = updatedProfile;
                }

                this.loading = false;
            },
            error: (err) => {
                console.error('Error approving profile:', err);
                this.toastr.error('Failed to approve profile.');
                this.loading = false;
            }
        });
    }


}


export function ageValidator(minAge: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const dobValue = control.value;
        if (!dobValue) return null; // no DOB entered yet

        const dob = new Date(dobValue);
        const today = new Date();

        let age = today.getFullYear() - dob.getFullYear();
        const monthDiff = today.getMonth() - dob.getMonth();
        const dayDiff = today.getDate() - dob.getDate();

        if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
            age--;
        }

        return age < minAge ? {ageTooLow: {actualAge: age, requiredAge: minAge}} : null;
    };
}





